import "./AddBoostFactorModal.scss"
import { Box, CircularProgress } from "@mui/material"
import React, {
  ReactElement,
  useState,
  useEffect,
  useMemo,
  useCallback,
} from "react"
import { POOLS_MAP, CRONOS_CRUISER_CONTRACT_ADDRESSES } from "src/constants"
import { useAction } from "src/hooks/useAction"
import { actions } from "src/state/xFer"
import { NftDetails } from "./types"
import { checkIdListChanged } from "./utils"
import {
  useGetBoostedNfts,
  useGetBoostedFarms,
  useEstTotalMultiplier,
} from "src/hooks/BoostFarming/useFarmBoostContract"
import {
  useFormatLayerForNftDetails,
  useFormatLayerForStakedNftDetails,
} from "src/hooks/BoostFarming/useNft"
import useFarmBoostHandler from "src/hooks/BoostFarming/useFarmBoostHandler"
import { getChainId } from "src/utils"

import { BoostedItem } from "./PoolItem"
import Button from "../Button"
import Text from "../Text"
import DrawerModalWrapper from "../DrawerModalWrapper"
import NftEmptyView from "./NftEmptyView"
import NftSelectorView from "./NftSelectorView"
import NftInfoView from "./NftInfoView"

enum Step {
  LOADING,
  Empty,
  Select,
  Confirm,
}

export default function NftStakingModal(): ReactElement {
  const [selectedNfts, setSelectedNfts] = useState<NftDetails[]>([])
  const [step, setStep] = useState<Step>(Step.LOADING)
  const { data: stakedNftData, isLoading: isStakeLoading } = useGetBoostedNfts()
  const stakedNftIds = useMemo(() => stakedNftData ?? [], [stakedNftData])
  const { data: myNfts, isLoading: isNftLoading } =
    useFormatLayerForNftDetails()
  const MINTED_CRUISER_URL = process.env.REACT_APP_MINTED_CRUISER_URL!
  const [selectedNftIds, setSelectedNftIds] = useState<string[]>(stakedNftIds)
  const { data: stakedNfts, isLoading: isStakeNftLoading } =
    useFormatLayerForStakedNftDetails(stakedNftIds)
  const myAllNfts = useMemo(() => {
    return [...stakedNfts, ...myNfts]
  }, [stakedNfts, myNfts])
  const { onStakeNft } = useFarmBoostHandler()
  const closeDepositModal = useAction(actions.setOpenedNftStakeModal, null)
  const { data: boostedPoolIds } = useGetBoostedFarms()
  const chainId = getChainId()
  const cronosCruiserAddr = CRONOS_CRUISER_CONTRACT_ADDRESSES[chainId]
  const { data: multiplier, refetch } = useEstTotalMultiplier(
    selectedNftIds.map((tokenId) => {
      return {
        tokenContract: cronosCruiserAddr,
        tokenId,
      }
    }),
  )
  useEffect(() => {
    if (stakedNftIds) {
      setSelectedNftIds(stakedNftIds)
    }
  }, [stakedNftIds])

  useEffect(() => {
    if (isNftLoading || isStakeLoading || isStakeNftLoading) {
      setStep(Step.LOADING)
    } else if (step === Step.LOADING) {
      if (myAllNfts.length === 0) {
        setStep(Step.Empty)
      } else {
        setStep(Step.Select)
      }
    }
  }, [isNftLoading, isStakeLoading, isStakeNftLoading, myAllNfts, step])

  const handleClick = useCallback(() => {
    if (step === Step.Empty) {
      window.open(MINTED_CRUISER_URL, "_blank")
    } else if (step === Step.Select) {
      setStep(Step.Confirm)
    } else if (step === Step.Confirm) {
      void onStakeNft(selectedNftIds, stakedNftIds).then(() =>
        closeDepositModal(),
      )
    }
  }, [
    MINTED_CRUISER_URL,
    closeDepositModal,
    onStakeNft,
    selectedNftIds,
    stakedNftIds,
    step,
  ])

  const renderBoostedPools = useMemo(() => {
    return Object.values(POOLS_MAP)
      .filter(
        ({ addresses, rewardPids }) =>
          (chainId ? addresses[chainId] : false) &&
          boostedPoolIds?.includes(rewardPids[chainId]!),
      )
      .map((pool) => (
        <BoostedItem
          key={pool.name}
          poolName={pool.name}
          multiplier={multiplier}
        />
      ))
  }, [boostedPoolIds, chainId, multiplier])

  const renderContentByStep = useMemo(() => {
    if (step === Step.LOADING) {
      return (
        <Box
          minWidth="300px"
          display="flex"
          flexDirection="column"
          alignItems="center"
          my={3}
        >
          <CircularProgress size={30} />
        </Box>
      )
    } else {
      return (
        <>
          <Box mb={3} className="divider" />
          {step === Step.Empty && <NftEmptyView />}
          {step === Step.Select && (
            <NftSelectorView
              dataList={myAllNfts}
              selectedNftIds={selectedNftIds}
              onChange={(selectedNfts) => {
                setSelectedNftIds(selectedNfts.map((nft) => nft.id))
                setSelectedNfts(selectedNfts)
                void refetch()
              }}
            />
          )}
          {step === Step.Confirm && <NftInfoView dataList={selectedNfts} />}
          <Box my={3} className="divider" />
          {step !== Step.Empty && (
            <>
              <Box
                mb={1}
                alignItems="center"
                display="flex"
                justifyContent="space-between"
              >
                <Box alignItems="center" display="flex">
                  <Text size="small">NFT selected</Text>
                </Box>
                <Text size="small">{selectedNftIds.length}/10</Text>
              </Box>
              <Box mb={2} display="flex" flexDirection="column" gap={0.5}>
                {renderBoostedPools}
              </Box>
            </>
          )}
          <Button
            disabled={
              step === Step.Select &&
              stakedNftIds &&
              !checkIdListChanged(selectedNftIds, stakedNftIds)
            }
            onClick={handleClick}
            fullWidth
          >
            {step === Step.Empty && "Take Me to Marketplaces"}
            {step === Step.Select && "Next and Review"}
            {step === Step.Confirm && "Confirm"}
          </Button>
        </>
      )
    }
  }, [
    handleClick,
    myAllNfts,
    refetch,
    renderBoostedPools,
    selectedNftIds,
    selectedNfts,
    stakedNftIds,
    step,
  ])

  return (
    <DrawerModalWrapper
      open
      onClose={closeDepositModal}
      title={step === Step.Confirm ? "Confirm NFT Stake" : "NFT Staking"}
    >
      {renderContentByStep}
    </DrawerModalWrapper>
  )
}
